'use client'

import { useEffect, useRef, useState } from 'react'
import { motion, useInView } from 'framer-motion'

const STEPS = [
  { n: '01', title: 'Brand Guidelines' },
  { n: '02', title: 'Sketches & Wireframes' },
  { n: '03', title: 'High-Fidelity Figma' },
  { n: '04', title: 'Next.js + Node.js Build' },
  { n: '05', title: 'On-Page SEO' },
] as const

const ROTATE_MS = 1600

export default function DesignProcessSteps() {
  const containerRef = useRef<HTMLOListElement | null>(null)
  const inView = useInView(containerRef, { amount: 0.4, once: false })
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (!inView) return
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % STEPS.length)
    }, ROTATE_MS)
    return () => window.clearInterval(id)
  }, [inView])

  return (
    <ol
      ref={containerRef}
      className="max-w-[1000px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5"
    >
      {STEPS.map(({ n, title }, i) => {
        const isActive = i === activeIndex
        return (
          <motion.li
            key={n}
            initial={false}
            animate={{
              backgroundColor: isActive ? '#7D40FF' : '#FFFFFF',
              borderColor: isActive ? '#7D40FF' : '#E7DBFF',
              y: isActive ? -4 : 0,
              boxShadow: isActive
                ? '0 18px 40px -16px rgba(125, 64, 255, 0.45)'
                : '0 1px 0 0 rgba(15, 15, 15, 0.02)',
            }}
            transition={{
              duration: 0.55,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="relative rounded-2xl border p-6 will-change-transform overflow-hidden"
          >
            <motion.span
              initial={false}
              animate={{ color: isActive ? '#FFFFFF' : '#B894FF' }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-3xl leading-none block mb-4"
            >
              {n}
            </motion.span>
            <motion.h3
              initial={false}
              animate={{ color: isActive ? '#FFFFFF' : '#0f0f0f' }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-base font-medium tracking-tight"
            >
              {title}
            </motion.h3>

            <motion.span
              aria-hidden
              initial={false}
              animate={{
                scaleX: isActive ? 1 : 0,
                opacity: isActive ? 1 : 0,
              }}
              transition={{
                duration: isActive ? ROTATE_MS / 1000 : 0.3,
                ease: isActive ? 'linear' : [0.22, 1, 0.36, 1],
              }}
              style={{ transformOrigin: 'left center' }}
              className="absolute left-0 bottom-0 h-[3px] w-full bg-white/70"
            />
          </motion.li>
        )
      })}
    </ol>
  )
}
